/* eslint-disable max-lines-per-function */
import { FieldBase } from '@/domain/mitarbeiter/features/anlageassistent/components/form/fieldComponents/FieldBase';
import { useAnlageassistentAppForm } from '@/domain/mitarbeiter/features/anlageassistent/components/form/hooks';
import { useAnlageassistentFormContext } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentFormContext';
import { useAnstellungsverhaeltnisSchema } from '@/domain/mitarbeiter/features/anlageassistent/hooks/schema/useAnstellungsverhaeltnisSchema';
import { FristenGrund } from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/fristenGrund';
import { ErinnerungAnMultiSelect } from '@/domain/mitarbeiter/shared/components/v2/ErinnerungenAnMultiSelect/ErinnerungenAnMultiSelect';
import { ErinnerungsZeitenMultiSelect } from '@/domain/mitarbeiter/shared/components/v2/ErinnerungsZeitenMultiSelect/ErinnerungsZeitenMultiSelect';
import { useProfile } from '@/hooks';
import { toast } from '@/hooks/use-toast';
import {
  Body,
  Button,
  FieldLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Skeleton,
  Switch,
} from '@e2n/ui/v2';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { Suspense } from 'react';
import z from 'zod';
import { useFristenContext } from '../context/useFristenContext';

export function CreateSheet() {
  const {
    openCreateSheet,
    setOpenCreateSheet,
    createSheetDefaultValues,
    setCreateSheetDefaultValues,
  } = useFristenContext();
  const { FristenSchema } = useAnstellungsverhaeltnisSchema();
  const form = useAnlageassistentFormContext();
  const { manager } = useProfile();

  const fristenForm = useAnlageassistentAppForm({
    defaultValues: {
      fristdatum: createSheetDefaultValues.fristdatum,
      grund: createSheetDefaultValues.grund,
      andererGrund: null,
      bemerkung: null,
      erinnerung: true,
      erinnerungsZeit: createSheetDefaultValues.erinnerungsZeit,
      erinnerungAn: manager?.id ? [manager.id] : [],
    } satisfies z.input<typeof FristenSchema> as z.input<typeof FristenSchema>,
    validators: {
      onSubmit: FristenSchema,
    },
    onSubmit: async ({ value }) => {
      form.pushFieldValue('anstellungsverhaeltnis.fristen', value);
      toast({
        title: t(
          'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.addFristDialog.message',
        ),
      });
      handleClose();
    },
  });

  function handleClose() {
    fristenForm.reset();
    setOpenCreateSheet(false);
    setCreateSheetDefaultValues({
      fristdatum: null,
      grund: null,
      erinnerungsZeit: [],
    });
  }

  return (
    <Sheet
      open={openCreateSheet}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        } else {
          setOpenCreateSheet(open);
        }
      }}>
      <SheetTrigger asChild>
        <Button variant={'ghost'} className="flex gap-2">
          <FontAwesomeIcon icon={faPlus} />
          <span>
            {t(
              'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.addFristDialog.buttonLabel',
            )}
          </span>
        </Button>
      </SheetTrigger>

      <SheetContent>
        <fristenForm.AppForm>
          <form
            className="h-full flex flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              fristenForm.handleSubmit();
            }}>
            <SheetHeader>
              <SheetTitle>
                {t(
                  'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.addFristDialog.title',
                )}
              </SheetTitle>
            </SheetHeader>
            <div className="px-4 py-6 flex flex-col gap-6 flex-1 overflow-auto">
              <Body className="text-neutrals-700">
                {t(
                  'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.addFristDialog.description',
                )}
              </Body>
              <fristenForm.AppField
                name="fristdatum"
                listeners={{
                  onChange: ({ value }) => {
                    const isFristInPast = dayjs(value)
                      .startOf('day')
                      .isBefore(dayjs(new Date()).startOf('day'), 'day');
                    if (isFristInPast) {
                      fristenForm.setFieldValue('erinnerung', false);
                      fristenForm.setFieldValue('erinnerungsZeit', []);
                      fristenForm.setFieldValue('erinnerungAn', []);
                    }
                  },
                }}>
                {(field) => (
                  <field.DatePickerInput
                    label={t(
                      'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.fristdatum.label',
                    )}
                    isRequired
                  />
                )}
              </fristenForm.AppField>
              <fristenForm.AppField name="grund">
                {(field) => (
                  <FieldBase
                    label={t(
                      'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.grund.label',
                    )}
                    isRequired>
                    <Select
                      onValueChange={(value) => {
                        field.handleChange(value);
                        if (value !== FristenGrund.ANDERER) {
                          fristenForm.setFieldValue('andererGrund', null);
                        }
                      }}
                      value={field.state.value ?? undefined}>
                      <SelectTrigger className="border-neutrals-400">
                        <SelectValue
                          placeholder={t(
                            'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.grund.placeholder',
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(FristenGrund).map((key) => (
                          <SelectItem key={key} value={key}>
                            {t(
                              `domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.grund.options.${key}`,
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldBase>
                )}
              </fristenForm.AppField>
              <fristenForm.Subscribe selector={(state) => state.values.grund}>
                {(grund) =>
                  grund === FristenGrund.ANDERER && (
                    <fristenForm.AppField name="andererGrund">
                      {(field) => (
                        <field.TextInput
                          label={t(
                            'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.andererGrund.label',
                          )}
                          placeholder={t(
                            'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.andererGrund.placeholder',
                          )}
                          isRequired
                          maxLength={50}
                        />
                      )}
                    </fristenForm.AppField>
                  )
                }
              </fristenForm.Subscribe>
              <fristenForm.AppField name="bemerkung">
                {(field) => (
                  <field.TextAreaInput
                    label={t(
                      'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.bemerkung.label',
                    )}
                    showCounter={true}
                    maxLength={250}
                  />
                )}
              </fristenForm.AppField>
              <fristenForm.Subscribe
                selector={(state) => state.values.fristdatum}>
                {(fristdatum) => {
                  const isFristInPast = dayjs(fristdatum).isBefore(new Date());
                  return (
                    <fristenForm.AppField name="erinnerung">
                      {(field) => (
                        <div className="flex items-center gap-2">
                          <Switch
                            id={field.name}

                            checked={field.state.value}
                            disabled={isFristInPast}
                            onCheckedChange={(value) => {
                              if (!value) {
                                fristenForm.setFieldValue(
                                  'erinnerungsZeit',
                                  [],
                                );
                                fristenForm.setFieldValue('erinnerungAn', []);
                              }
                              field.handleChange(value);
                            }}
                          />
                          <FieldLabel htmlFor={field.name}>
                            {t(
                              'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.erinnerung.label',
                            )}
                          </FieldLabel>
                        </div>
                      )}
                    </fristenForm.AppField>
                  );
                }}
              </fristenForm.Subscribe>
              <fristenForm.Subscribe
                selector={(state) => ({
                  erinnerung: state.values.erinnerung,
                  fristdatum: state.values.fristdatum,
                })}>
                {({ erinnerung, fristdatum }) =>
                  erinnerung && (
                    <div className="flex flex-col gap-6">
                      <fristenForm.AppField name="erinnerungsZeit">
                        {(field) => (
                          <FieldBase
                            label={t(
                              'domain.mitarbeiter.fristen.erinnerungsZeit.label',
                            )}
                            isRequired>
                            <ErinnerungsZeitenMultiSelect
                              title={t(
                                'domain.mitarbeiter.fristen.erinnerungsZeit.label',
                              )}
                              id={field.name}
                              values={field.state.value}
                              handleChange={(value) =>
                                field.handleChange(value)
                              }
                              fristdatum={fristdatum ?? undefined}
                            />
                          </FieldBase>
                        )}
                      </fristenForm.AppField>
                      <fristenForm.AppField name="erinnerungAn">
                        {(field) => (
                          <FieldBase
                            label={t(
                              'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.erinnerungAn.label',
                            )}
                            isRequired>
                            <Suspense
                              fallback={<Skeleton className="w-full h-10" />}>
                              <ErinnerungAnMultiSelect
                                values={field.state.value}
                                handleChange={(value) =>
                                  field.handleChange(value)
                                }
                              />
                            </Suspense>
                          </FieldBase>
                        )}
                      </fristenForm.AppField>
                    </div>
                  )
                }
              </fristenForm.Subscribe>
            </div>
            <SheetFooter>
              <fristenForm.SubmitButton buttonText={t('actions.fertig')} />
            </SheetFooter>
          </form>
        </fristenForm.AppForm>
      </SheetContent>
    </Sheet>
  );
}
